import {
  ArrowLeftIcon,
  BurgerNavIcon,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  Collapse,
  Skeleton,
  Tag,
  Typography,
  useMediaQuery,
  UserIcon
} from '@ag1/ui';
import { usePageContext } from '@portal/processes/Page/PageContext';
import { useHeaderContext } from '@portal/state/context/HeaderContext';
import { productCardClickEvent } from 'analytics/events/shop';
import { navigationClickEvent } from 'analytics/events/studio';
import { useAnalyticsHashedEmail } from 'analytics/hashed-email';
import { useDeferredSessionValue } from 'appLayer/auth';
import { useScrollPaddingTopForType, useScrollPaddingTopHandler } from 'atoms/scrollPaddingTopAtom';
import clsx from 'clsx';
import { routes } from 'constants/routes';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArticleProgressBar from 'shared/studio/ArticleBody/ArticleProgressBar/ArticleProgressBar';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { type getActiveBannerForHeader } from 'shared/utils/bannerVisibility';
import { stripUrlHash } from 'state/utils/urlHelpers';
import { NavigationLinkType, type HeaderNavLink } from 'types/studio/header';
import {
  HeaderFullV2,
  type PromoBanner as PromoBannerType,
  type Header as HeaderType,
  type NavigationLink,
  type SanityKeyed
} from 'types/studio/studio';
import { CommonSectionProps } from '@portal/shared/studio/types';
import { ActionButton, CartButton } from './ActionButton/ActionButton';
import { Banner, BannerType } from './Banner/Banner';
import HeaderAccountDropdown from './HeaderAccountDropdown/HeaderAccountDropdown';
import styles from './Header.module.scss';
import HeaderLogo from './Logo/Logo';
import HeaderMenuLink from './LinkElement/LinkElement';
import { PromoBanner } from './PromoBanner/PromoBanner';

type HeaderProps = {
  header: HeaderType;
  logoSVG?: string;
  hasProgressBar?: boolean;
  newThemeProgressBar?: boolean;
  activeBanner: ReturnType<typeof getActiveBannerForHeader>;
  activePromoBanner?: PromoBannerType;
} & Omit<HeaderFullV2, '_type'>;

type NavigationLinkNestedLink = SanityKeyed<
  Pick<NavigationLink, 'showNewTag' | 'text' | 'navLink' | 'navPage'>
>;
type NavSubmenuItem = { text: string; image: any } & Pick<NavigationLink, 'navLink' | 'navPage'>;
type NavSubmenuListItem = Omit<
  NonNullable<NonNullable<HeaderType['navV2Menu']>[number]['navSubmenuList']>[number],
  '_type'
>;
type NavSubmenuListItemNested = NavSubmenuListItem & { _type: 'navigationLink' };

const NewTag = () => (
  <Tag type="go-green" size="s" showIcon={false} className={styles.restRecoveryTag}>
    New
  </Tag>
);

const NavGridItemContent: React.FC<{
  className?: string;
  textClassName?: string;
  item: NavSubmenuItem;
  onClick?: () => void;
}> = ({ className, textClassName, item: { text, navLink, navPage, image }, onClick }) => {
  const href = navPage?.slug?.current || navLink || '';

  const handleClick = () => {
    productCardClickEvent({
      productId: navPage?.ref || navLink || '',
      productName: text,
      location: 'header_navigation'
    });
    onClick?.();
  };

  return (
    <Link className={clsx(styles.imageLink, className)} href={href} onClick={handleClick}>
      <div className={styles.imageContainer}>
        <StudioBaseImageV2
          image={image}
          sizes={{ xxs: '168px', l: '250px' }}
          className={styles.thumbnail}
        />
      </div>
      <span className={clsx(styles.lineClamp, textClassName)}>{text}</span>
    </Link>
  );
};

const MobileMenuItem: React.FC<{
  item: NonNullable<HeaderType['navV2Menu']>[number];
  normalizedPath: string;
  handleClick?: () => void;
  handleSubmenuClick?: (submenu: NavSubmenuListItem) => void;
  handleResetView?: () => void;
  isNestedView?: boolean;
}> = ({
  item: {
    expandMobileSubmenu,
    type,
    text,
    navSubmenuList = [],
    navPage,
    navLink = '',
    ...restItem
  },
  normalizedPath,
  handleClick,
  handleSubmenuClick,
  handleResetView,
  isNestedView = false
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navSubmenuItems = useMemo(
    () =>
      (restItem.navSubmenu || []).reduce(
        (acc, i) => {
          const item = {
            ...i,
            image: i.image,
            text: i.text || '',
            href: i.navPage?.slug?.current || i.navLink || ''
          };
          if (item.image && item.image.asset) {
            acc.gridItems.push(item);
          } else {
            acc.listItems.push(item);
          }
          return acc;
        },
        {
          listItems: [] as NavSubmenuItem[],
          gridItems: [] as NavSubmenuItem[]
        }
      ),
    [restItem.navSubmenu]
  );

  const navSubmenuTitle = restItem.navSubmenuTitle?.trim() || '';
  const expandMenu = expandMobileSubmenu || menuOpen;

  const expandedSubmenuText = isNestedView ? (
    <Button
      component="div"
      className={clsx(styles.mobileMenuHeaderDense, styles.mobileMenuHeaderDenseNested)}
      style={{ marginBottom: `8px` }}
      variant="link"
      size="small"
      noPadding
      onClick={handleResetView}
      icon={<ArrowLeftIcon />}
      iconPosition="left">
      {text}
    </Button>
  ) : (
    <div className={styles.mobileMenuHeaderDense} style={{ marginBottom: `8px` }}>
      {text}
    </div>
  );

  if (type === 'submenu') {
    return (
      <li key={text}>
        {expandMobileSubmenu ? (
          expandedSubmenuText
        ) : (
          <Button
            onClick={() => setMenuOpen((prev) => !prev)}
            className={clsx(styles.mobileMenuItem, styles.mobileSubMenuItem)}>
            <div className={styles.buttonContent}>
              <span>{text}</span>
              <ChevronDownIcon
                className={clsx(styles.expandIcon, menuOpen ? styles.opened : styles.closed)}
              />
            </div>
          </Button>
        )}
        <Collapse isOpened={expandMenu}>
          <div className={clsx(styles.subNavigation, expandMobileSubmenu && styles.alwaysExpand)}>
            <div
              className={styles.bottom}
              style={
                expandMobileSubmenu
                  ? {}
                  : {
                      paddingTop: `8px`,
                      gap: `4px`,
                      paddingBottom: 0
                    }
              }>
              {navSubmenuList.map((submenu) => {
                // Submenu - no link, update parent state to show nested view
                if (submenu.type === 'submenu') {
                  return (
                    <HeaderMenuLink
                      key={`list-primary-${text}-${submenu.text}`}
                      href="#"
                      className={clsx(
                        normalizedPath === submenu.navLink && styles.active,
                        expandMobileSubmenu && styles.mobileMenuItemPrimary,
                        styles.nestedMobileMenuItemPrimary
                      )}
                      onClick={() => handleSubmenuClick?.(submenu)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {submenu.text}
                        {submenu.showNewTag && <NewTag />}
                      </div>
                      <ChevronRightIcon />
                    </HeaderMenuLink>
                  );
                }
                // Standard link / page
                return (
                  <HeaderMenuLink
                    key={`list-primary-${text}-${submenu.text}`}
                    className={clsx(
                      normalizedPath === submenu.navLink && styles.active,
                      expandMobileSubmenu && styles.mobileMenuItemPrimary
                    )}
                    href={submenu.navLink || ''}
                    onClick={handleClick}>
                    {submenu.text}
                    {submenu.showNewTag && <NewTag />}
                  </HeaderMenuLink>
                );
              })}
              {!!navSubmenuTitle.trim() && (
                <div className={styles.mobileMenuHeaderDense} style={{ marginTop: `16px` }}>
                  {navSubmenuTitle}
                </div>
              )}
              {navSubmenuItems.listItems.map((submenu) => {
                if (!submenu.text.trim()) {
                  return null;
                }
                const href = submenu.navPage?.slug?.current || submenu.navLink || '';
                return (
                  <HeaderMenuLink
                    key={`list-secondary-${text}-${submenu.text}`}
                    className={clsx(
                      styles.mobileMenuItemSecondary,
                      normalizedPath === href && styles.active
                    )}
                    href={href}
                    onClick={handleClick}>
                    {submenu.text}
                  </HeaderMenuLink>
                );
              })}
              {navSubmenuItems.gridItems.length > 0 && (
                <>
                  <div className={styles.cards}>
                    {navSubmenuItems.gridItems.map((submenu) => {
                      return (
                        <NavGridItemContent
                          key={`grid-${text}-${submenu.text}`}
                          className={styles.mobileGridItem}
                          textClassName={styles.mobileGridItemText}
                          item={submenu}
                          onClick={handleClick}
                        />
                      );
                    })}
                  </div>
                  {!isNestedView && <div className={styles.cardsDivider} />}
                </>
              )}
            </div>
          </div>
        </Collapse>
      </li>
    );
  }

  const normalizedLink = navPage?.slug?.current || navLink;

  return (
    <li key={text}>
      <HeaderMenuLink href={normalizedLink} className={styles.mobileMenuItem} onClick={handleClick}>
        <span
          className={clsx(
            styles.menuItemText,
            normalizedPath === normalizedLink ? styles.active : ''
          )}>
          {text}
        </span>
      </HeaderMenuLink>
    </li>
  );
};

// Add customer here to add info to the account section
const MobileMenu = ({
  className,
  navV2Menu = [],
  signInText,
  handleClose,
  customer,
  stackable
}: {
  className: string;
  navV2Menu: HeaderType['navV2Menu'];
  signInText: string;
  handleClose: () => void;
  customer: any;
  stackable: boolean | undefined;
}) => {
  const [nestedViewOpen, setNestedViewOpen] = useState(false);
  const [selectedNestedMenu, setSelectedNestedMenu] = useState<NonNullable<
    HeaderType['navV2Menu']
  > | null>(null);

  const router = useRouter();

  // Grab the total height of our headers so we can render this menu at the
  // bottom-edge of them.
  const totalHeaderHeight = useScrollPaddingTopForType(null, 'css');
  const navigationStyles = useMemo<React.CSSProperties>(() => {
    if (totalHeaderHeight) {
      return {
        position: `fixed`,
        top: totalHeaderHeight,
        bottom: 0
      };
    }
    return {};
  }, [totalHeaderHeight]);

  const normalizedPath = useMemo(() => stripUrlHash(router.asPath), [router.asPath]);

  const resetNestedView = useCallback(() => {
    setNestedViewOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    setTimeout(() => {
      resetNestedView();
      handleClose();
    }, 400);
  }, [handleClose, resetNestedView]);

  const accountDisplay = customer ? (
    <HeaderAccountDropdown customer={customer} handleClick={handleClick} />
  ) : (
    <div className={clsx(styles.section, styles.account)}>
      <HeaderMenuLink href={routes.root} className={styles.mobileMenuItem}>
        <span className={styles.menuItemTextAccount}>{signInText}</span>
        <UserIcon />
      </HeaderMenuLink>
    </div>
  );

  return (
    <nav
      className={clsx(styles.menuWrapper, className, stackable && styles.stackableMobileMenu)}
      style={navigationStyles}>
      {/* Standard View */}
      <div className={clsx(styles.slidingNestedContainer, nestedViewOpen && styles.opened)}>
        <div className={styles.slidingItem}>
          {accountDisplay}
          <div className={clsx(styles.section, styles.navigation)}>
            <ul className={styles.top}>
              {navV2Menu.map((item) => (
                <MobileMenuItem
                  key={item._key || item.text}
                  item={item}
                  normalizedPath={normalizedPath}
                  handleClick={handleClick}
                  handleSubmenuClick={(submenu) => {
                    if (submenu) {
                      const selectedMenu = [submenu as NavSubmenuListItemNested];
                      setNestedViewOpen(true);
                      setSelectedNestedMenu(selectedMenu);
                    }
                  }}
                  handleResetView={resetNestedView}
                />
              ))}
            </ul>
          </div>
        </div>
        {/* Nested View */}
        <div className={styles.slidingItem}>
          <div className={styles.navigation}>
            <ul className={styles.top}>
              {selectedNestedMenu?.map((item) => (
                <MobileMenuItem
                  key={item._key || item.text}
                  item={item}
                  normalizedPath={normalizedPath}
                  handleClick={handleClick}
                  handleResetView={resetNestedView}
                  isNestedView
                />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

type MenuItemLinkProps = {
  showNewTag?: boolean;
  type: NavigationLinkType;
  text: string;
  navLink?: string;
  navPage?: HeaderNavLink;
  vertical?: boolean;
  normalizedPath: string;
};

const MenuItemLink: React.FC<MenuItemLinkProps> = ({
  showNewTag,
  type,
  text,
  navLink,
  navPage,
  vertical = false,
  normalizedPath
}) => {
  const normalizedLink =
    (type === NavigationLinkType.page ? navPage?.slug?.current : navLink) || '';

  return (
    <div className={clsx(styles.nav__menuitem_container, vertical && styles.vertical)}>
      <HeaderMenuLink href={normalizedLink} className={styles.menuItem}>
        <span
          className={clsx(styles.menuItem__contents, {
            [styles.active]: normalizedPath === normalizedLink
          })}>
          {text}
          {showNewTag && <NewTag />}
        </span>
      </HeaderMenuLink>
    </div>
  );
};

const DropdownContent = ({
  listContent,
  gridContent,
  navSubmenuTitle,
  navSubmenuListTitle
}: {
  listContent: NavigationLinkNestedLink[];
  gridContent: NavSubmenuItem[];
  navSubmenuTitle: string;
  navSubmenuListTitle: string;
}) => {
  const router = useRouter();
  const normalizedPath = useMemo(() => stripUrlHash(router.asPath), [router.asPath]);

  const listContentCount = listContent.length;
  const listContentCountPerColumn = 5;
  const chunkedListContent = useMemo(
    () =>
      listContent.reduce(
        (acc, i) => {
          if (!acc.length) {
            acc.push([i]);
            return acc;
          }
          const lastIndex = acc.length - 1;
          if (acc[lastIndex].length === listContentCountPerColumn) {
            acc.push([i]);
          } else {
            acc[lastIndex].push(i);
          }
          return acc;
        },
        [] as (typeof listContent)[]
      ),
    [listContent]
  );

  const clampedGridContent = useMemo(
    () => gridContent.slice(0, 5 - Math.ceil(listContentCount / listContentCountPerColumn)),
    [gridContent, listContentCount]
  );

  return (
    <div className={styles.dropdownContainer}>
      <div className={styles.subContainer}>
        {navSubmenuListTitle && (
          <Typography
            variant="captionMono"
            size="1"
            noPadding
            className={styles.subContainerHeader}>
            {navSubmenuListTitle}
          </Typography>
        )}
        <ul>
          {chunkedListContent.map((chunk) => (
            <li key={chunk.map(({ text }) => text).join('+')} className={styles.dropdownListItems}>
              {chunk.map(
                ({ _key, showNewTag, text, navLink, navPage }) =>
                  !!text && (
                    <MenuItemLink
                      key={_key || `${text}-${navLink}`}
                      showNewTag={showNewTag}
                      type={NavigationLinkType.link}
                      text={text}
                      navLink={navLink}
                      navPage={navPage}
                      vertical
                      normalizedPath={normalizedPath}
                    />
                  )
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.subContainer}>
        {navSubmenuTitle && (
          <Typography
            variant="captionMono"
            size="1"
            noPadding
            className={styles.subContainerHeader}>
            {navSubmenuTitle}
          </Typography>
        )}
        <ul>
          {clampedGridContent.map((i) => (
            <li key={i.text} className={styles.alignSelfStart}>
              <NavGridItemContent item={i} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const MenuItem: React.FC<
  MenuItemLinkProps & {
    showNavChevrons?: boolean;
    navSubmenuTitle?: string;
    navSubmenu?: any[];
    navSubmenuListTitle?: string;
    navSubmenuList?: NavigationLinkNestedLink[];
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    showUnderline?: boolean;
  }
> = ({
  showNavChevrons = false,
  navSubmenuTitle = '',
  navSubmenu = [],
  navSubmenuListTitle = '',
  navSubmenuList = [],
  onMouseEnter,
  onMouseLeave,
  showUnderline,
  ...menuItemLinkProps
}) => {
  const { showNewTag, type, text, navLink } = menuItemLinkProps;
  return type === 'submenu' ? (
    <li
      className={clsx(styles.nav__menuitem_container, { [styles.withSubmenu]: navSubmenu })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <HeaderMenuLink
        href={navLink || ''}
        delinkOnEmpty
        className={clsx(styles.menuItem, styles.menuItemButton)}>
        <span
          className={clsx(styles.menuItem__contents, { [styles.showUnderline]: showUnderline })}>
          {text}
          {showNewTag && <NewTag />}
          {showNavChevrons && <ChevronDownIcon className={styles.expandIcon} />}
        </span>
      </HeaderMenuLink>
      {navSubmenu && (
        <div role="menu" className={styles.nav__menu_dropdown}>
          <DropdownContent
            listContent={navSubmenuList}
            gridContent={navSubmenu}
            navSubmenuTitle={navSubmenuTitle}
            navSubmenuListTitle={navSubmenuListTitle}
          />
        </div>
      )}
    </li>
  ) : (
    <li>
      <MenuItemLink {...menuItemLinkProps} />
    </li>
  );
};

const MegaMenu: React.FC<{
  className: string;
  navV2: any[];
  showNavChevrons?: boolean;
  isLoadingHeader: boolean;
}> = ({ className, navV2, showNavChevrons = false, isLoadingHeader }) => {
  const router = useRouter();
  const normalizedPath = useMemo(() => stripUrlHash(router.asPath), [router.asPath]);
  const [activeItemText, setActiveItemText] = useState<string | null>(null);

  return (
    <nav className={className}>
      <ul className={styles.nav__menuitems}>
        {isLoadingHeader
          ? Array.from({ length: 3 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i} className={clsx(styles.nav__menuitem_container)}>
                <div className={clsx(styles.menuItem, styles.menuItemButton)}>
                  <Skeleton width="128px" height="32px" variant="dark" />
                </div>
              </li>
            ))
          : navV2.map(
              ({
                showNewTag,
                type,
                text,
                navSubmenuTitle,
                navSubmenu,
                navSubmenuListTitle,
                navSubmenuList,
                navLink,
                navPage
              }) => (
                <MenuItem
                  key={text}
                  showNavChevrons={showNavChevrons}
                  {...{
                    showNewTag,
                    type,
                    text,
                    navSubmenuTitle,
                    navSubmenu: navSubmenu || [],
                    navSubmenuListTitle,
                    navSubmenuList: navSubmenuList || [],
                    navLink,
                    navPage,
                    onMouseEnter: () => setActiveItemText(text),
                    onMouseLeave: () => setActiveItemText(null),
                    showUnderline: activeItemText === text,
                    normalizedPath
                  }}
                />
              )
            )}
      </ul>
    </nav>
  );
};

export const Header = ({
  hasDifferentButtons,
  logoLink,
  header,
  logoSVG,
  newThemeProgressBar,
  hasProgressBar,
  marketingButton,
  activeBanner,
  activePromoBanner,
  stackable
}: HeaderProps & CommonSectionProps) => {
  const { sectionsUsed } = usePageContext();
  const {
    headerData: { isBootstrapped: isHeaderBootstrapped }
  } = useHeaderContext();

  const isNavigation = sectionsUsed?.navigation || false;
  const scrollPaddingTopType = isNavigation ? 'navigation' : 'header';
  const bannerType = `pre-${scrollPaddingTopType}` as const;

  const skipButtonRef = useRef<HTMLDivElement | null>(null);
  const skipButtonShown = useRef(false);
  const headerRef = useRef<HTMLDivElement | null>(null);

  useAnalyticsHashedEmail();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useDeferredSessionValue();
  const customer = session?.customer;
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [skipButtonHeight, setSkipButtonHeight] = useState(0);

  const atSmall = useMediaQuery('s'); // >= 768
  const atMedium = useMediaQuery('m'); // >= 1024

  const sendNavigationClickEvent = (location: string) => {
    navigationClickEvent({ location, navigationType: 'link' });
  };

  const headerLogoLink =
    hasDifferentButtons && logoLink?.length ? logoLink : header?.logo?.link || '/';

  const headerMarketingButton = () =>
    hasDifferentButtons && marketingButton?.marketingLink
      ? marketingButton
      : header?.marketingButton;

  const marketingButtonData = {
    topButton: headerMarketingButton()?.topButton,
    marketingLink: headerMarketingButton()?.marketingLink,
    showArrowRight: false,
    hideIconSize: 'small'
  };

  const isAuthorized = Boolean(customer?.id);
  const button = isAuthorized ? header.authorizedActionButton : header.unauthorizedActionButton;

  const navV2 = useMemo(() => {
    return (header.navV2 || []).map((navItem) => {
      const { navLink, hasAuthConfig, authConfig } = navItem;

      return {
        ...navItem,
        navSubmenuList: navItem.navSubmenuList || [],
        navSubmenu: navItem.navSubmenu || [],
        ...(hasAuthConfig && authConfig && !!customer
          ? {
              ...authConfig,
              navSubmenuList: authConfig.navSubmenuList || [],
              navSubmenu: authConfig.navSubmenu || []
            }
          : {}),
        _type: navItem._type,
        navLink
      };
    });
  }, [header.navV2, customer]);

  const navV2Menu = useMemo(() => {
    return header.navV2Menu?.map((navItem) => {
      const { hasAuthConfig, authConfig } = navItem;

      return {
        ...navItem,
        navSubmenuList: navItem.navSubmenuList || [],
        navSubmenu: navItem.navSubmenu || [],
        ...(hasAuthConfig && authConfig && !!customer
          ? {
              ...authConfig,
              navSubmenuList: authConfig.navSubmenuList || [],
              navSubmenu: authConfig.navSubmenu || []
            }
          : {}),
        _type: navItem._type
      };
    });
  }, [customer, header.navV2Menu]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Tab' && !skipButtonShown.current) {
      setShowSkipButton(true);
      // Make skip button to show only on first "Tab" press
      skipButtonShown.current = true;
    }
  }, []);

  const handleSkipButtonBlur = useCallback(() => setShowSkipButton(false), []);

  const handleSkipButtonClick = useCallback(() => {
    const main = document.querySelector('[data-content="main"]');
    if (!main) return;

    const focusSelector = 'a, button, input, textarea, select, [tabindex]';

    // Ignore the header (main's first child)
    const children = Array.from(main.children).slice(1);

    const focusable = children
      .map((child) => child.querySelector<HTMLElement>(focusSelector))
      .find((el): el is HTMLElement => !!el);

    focusable?.focus();
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (showSkipButton) {
      if (skipButtonRef.current) setSkipButtonHeight(skipButtonRef.current.offsetHeight);
    } else {
      setSkipButtonHeight(0);
    }
  }, [showSkipButton]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add(styles.scrollLock);
    } else {
      document.body.classList.remove(styles.scrollLock);
    }

    return () => {
      document.body.classList.remove(styles.scrollLock);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen && atMedium) {
      setMobileMenuOpen(false);
    }
  }, [mobileMenuOpen, atMedium]);

  const { height: headerHeight, top: headerTop } = useScrollPaddingTopHandler(
    { type: scrollPaddingTopType },
    headerRef
  );

  const handleClose = useCallback(() => setMobileMenuOpen(false), []);

  return (
    <>
      {showSkipButton && (
        <div ref={skipButtonRef} className={styles.skipButtonContainer}>
          <button
            type="button"
            className={styles.skipButton}
            tabIndex={0}
            onBlur={handleSkipButtonBlur}
            onClick={handleSkipButtonClick}>
            Skip to main content
          </button>
        </div>
      )}
      <div className={styles.overlay} style={{ display: mobileMenuOpen ? 'block' : 'none' }} />
      {activeBanner && (
        <Banner
          banner={{
            _type: 'banner',
            headline: activeBanner.headline,
            notice: activeBanner.notice,
            noticeColor: activeBanner.noticeColor,
            image: activeBanner.startIcon,
            body: activeBanner.body,
            countries: activeBanner.countries || [],
            buttonText: activeBanner.buttonText
          }}
          bannerType={BannerType.AG1_HAT_D3}
          link={activeBanner.ctaUrl}
          scrollPaddingHeaderType={bannerType}
          asStatic={isNavigation}
          skipButtonHeight={skipButtonHeight}
        />
      )}
      {activePromoBanner && <PromoBanner {...activePromoBanner} />}
      {!isNavigation && (
        <div
          className={clsx(
            styles.navBlock,
            !!activeBanner && styles.activeBanner,
            stackable && styles.stackableBlock
          )}
          style={
            headerHeight
              ? {
                  height: `${headerHeight + skipButtonHeight}px`
                }
              : {}
          }
        />
      )}
      <header
        id="nav__header"
        ref={headerRef}
        className={clsx(
          styles.nav__header,
          !!activeBanner && styles.activeBanner,
          stackable && styles.stackableHeader,
          isNavigation && styles.headerGroupItem
        )}
        style={
          isNavigation
            ? undefined
            : ({
                '--skip-button-height': `${skipButtonHeight}px`,
                top: headerTop
              } as React.CSSProperties)
        }>
        {/* Tippy Goes here */}
        <div className={styles.nav__container}>
          <HeaderMenuLink
            className={styles.logo}
            href={headerLogoLink}
            onClick={() => sendNavigationClickEvent('header_logo')}>
            <HeaderLogo header={header} logoSVG={logoSVG} isWhite={false} />
          </HeaderMenuLink>
          <MegaMenu
            className={
              header.collapseNavigation ? styles.megaMenuCollapseOn : styles.megaMenuCollapseOff
            }
            navV2={navV2}
            showNavChevrons={header.showNavChevrons}
            isLoadingHeader={!isHeaderBootstrapped}
          />
          <div className={styles.nav__action}>
            <ActionButton button={button} marketingButton={marketingButtonData} />

            <CartButton isAuthorized={isAuthorized} />

            <div
              className={clsx(
                styles.accountWrapper,
                !!customer && styles.authenticatedAccountWrapper
              )}
              onMouseEnter={() => setShowAccountDropdown(true)}
              onMouseLeave={() => setShowAccountDropdown(false)}>
              {customer ? (
                <div
                  className={clsx(
                    styles.accountWrapperInternal,
                    styles.authenticatedAccountWrapperInternal,
                    showAccountDropdown && styles.accountWrapperInternalActive
                  )}>
                  <Button className={styles.profile} aria-label="Account Menu">
                    <UserIcon />
                    <span className={styles.signInText}>
                      {header.customerNamePrefix}
                      {customer.firstName}
                    </span>
                    <ChevronDownIcon className={styles.expandIcon} />
                  </Button>
                </div>
              ) : (
                <HeaderMenuLink
                  href={routes.root}
                  className={styles.signInTextMenuLink}
                  onClick={() => sendNavigationClickEvent('header_account')}>
                  <div className={clsx(styles.signInTextWrapper, styles.accountWrapperInternal)}>
                    <UserIcon />
                  </div>
                </HeaderMenuLink>
              )}
              {customer && <HeaderAccountDropdown customer={customer} show={showAccountDropdown} />}
            </div>

            <div
              className={
                header.collapseNavigation
                  ? styles.mobileMenuCollapseOn
                  : styles.mobileMenuCollapseOff
              }>
              <button
                type="button"
                className={styles.burgerButton}
                aria-label={mobileMenuOpen ? 'Close Menu' : 'Open Menu'}
                onClick={() => setMobileMenuOpen((prev) => !prev)}>
                <BurgerNavIcon
                  className={clsx(styles.burgerIcon, mobileMenuOpen ? styles.opened : '')}
                />
              </button>
              <MobileMenu
                className={clsx(mobileMenuOpen ? styles.opened : '')}
                navV2Menu={navV2Menu}
                signInText={header.signInText as string}
                handleClose={handleClose}
                customer={customer}
                stackable={stackable || isNavigation}
              />
            </div>
          </div>
        </div>
        {hasProgressBar && (
          <ArticleProgressBar sticky={!atSmall} newThemeProgressBar={newThemeProgressBar} />
        )}
      </header>
    </>
  );
};

export default Header;
